<!-- Lab Provider Selection (hidden in chips mode or when showLabProvider is false) -->
<div class="lab-provider-section" *ngIf="labProviders.length > 1 && mode !== 'chips' && showLabProvider">
  <mat-form-field appearance="outline" class="provider-selector">
    <mat-label>Lab Provider</mat-label>
    <mat-select [(value)]="selectedProvider" (selectionChange)="onProviderChange($event)">
      <mat-option *ngFor="let provider of labProviders" [value]="provider.id">
        {{ provider.name }}
      </mat-option>
    </mat-select>
  </mat-form-field>
</div>

<!-- Category filter removed - no categorization -->

<!-- Search Input (hidden in chips mode) -->
<mat-form-field class="full-width m-b-20" appearance="outline" *ngIf="mode !== 'chips' && showSearch">
  <mat-label>Search {{pluralTitle}}</mat-label>
  <input #inputElement matInput [placeholder]="disableAdding ? '' : 'Search for ' + singularTitle + '...'"
    [formControl]="testControl" [matAutocomplete]="auto" />

  <mat-autocomplete #auto="matAutocomplete" (optionSelected)="addToChecklist($event.option.value)">
    <mat-option *ngFor="let test of filteredLabTests | async" [value]="test">
      <div class="test-option">
        <span class="test-code">{{ test.orderCode }}</span>
        <span class="test-name">{{ test.name }}</span>
        <span class="test-mnemonic" *ngIf="test.mnemonic">({{ test.mnemonic }})</span>
        <span class="test-type" *ngIf="test.type">[{{ test.type }}]</span>
        <span class="micro-badge" *ngIf="test.isMicro" matTooltip="Microbiology test - requires specimen source">
          <mat-icon class="micro-icon">science</mat-icon>
          <span class="micro-text">Micro</span>
        </span>
      </div>
    </mat-option>
    <mat-option *ngIf="(filteredLabTests | async)?.length === 0 && testControl.value">
      Can't find the test you are looking for? <a (click)="openContactUsDialog($event)">Contact Us.</a>
    </mat-option>
  </mat-autocomplete>

  <mat-hint *ngIf="hintText">{{hintText}}</mat-hint>
</mat-form-field>

<!-- Checklist Display -->
<div class="checklist-container" *ngIf="showChecklist && checklistItemsInternal.length > 0">
  <div class="checklist-items-wrapped">
    <ng-container *ngFor="let item of checklistItemsInternal">
      <mat-checkbox [checked]="item.checked" (change)="onChecklistItemChange(item, $event.checked)"
        class="checklist-checkbox" [matTooltip]="getDetailedTooltip(item.test)" matTooltipPosition="above">
        <span class="test-mnemonic-only">{{ item.test.mnemonic || item.test.orderCode }}</span>
      </mat-checkbox>

      <!-- Specimen Source Autocomplete for isMicro items - inline after checkbox -->
      <mat-form-field *ngIf="item.test.isMicro" appearance="outline" class="specimen-source-field-inline">
        <mat-label>Specimen Source</mat-label>
        <input matInput [formControl]="getSpecimenSourceControl(item)" 
          [matAutocomplete]="auto"
          placeholder="Select or type custom value">
        <mat-autocomplete #auto="matAutocomplete"
          (optionSelected)="onSpecimenSourceChange(item, $event.option.value)"
          [displayWith]="displaySpecimenSource">
          <mat-option *ngFor="let source of getFilteredSpecimenSources(getSpecimenSourceControl(item)) | async"
            [value]="source">
            {{ source }}
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </ng-container>
  </div>
</div>

<!-- Chip Display -->
<div *ngIf="mode === 'chips'">
  <!-- Single chip list - no categorization -->
  <mat-form-field class="full-width m-b-20" appearance="outline">
    <mat-label>{{pluralTitle}}</mat-label>
    <mat-chip-grid #chipGrid cdkDropList (cdkDropListDropped)="onDrop($event)"
      (cdkDropListDragStarted)="onDragStarted()" (cdkDropListDragEnded)="onDragEnded()">
      <mat-chip-row *ngFor="let test of testChips; let i = index" (removed)="removeChip(i, test)" [editable]="false"
        (edited)="editChip(test, $event)" appTouchEdit (dblclick)="handleDblClick(test, i)"
        [matTooltip]="providerTooltip.computedTooltip" #providerTooltip="appProviderInitialsTooltip"
        [appProviderInitialsTooltip]="test" [existingTooltip]="getTooltipText(test)" cdkDrag
        (cdkDragStarted)="onDragStarted()" (cdkDragEnded)="onDragEnded()" [cdkDragDisabled]="!enableDragAndDrop"
        class="drag-chip">

        <!-- Drag handle icon -->
        <mat-icon *ngIf="enableDragAndDrop" cdkDragHandle class="drag-handle" [matTooltip]="'Drag to reorder'">
          {{ getDragHandleIcon() }}
        </mat-icon>

        <span class="test-code">{{ test.orderCode }} </span>
        <span class="test-name">{{ truncateDescription(test.name) }}</span>
        <span class="test-type" *ngIf="test.type">({{ test.type }})</span>
        <button matChipRemove [attr.aria-label]="'remove ' + test.orderCode">
          <mat-icon>cancel</mat-icon>
        </button>
      </mat-chip-row>
      <input #inputElement [placeholder]="disableAdding ? '' : 'Add ' + singularTitle + '...'"
        [matChipInputFor]="chipGrid" [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
        [matChipInputAddOnBlur]="addOnBlur" [formControl]="testControl" [matAutocomplete]="auto"
        (matChipInputTokenEnd)="onChipInputTokenEnd($event)" (matChipInputAdd)="onChipInputAdd($event)"
        *ngIf="showSearch" />
    </mat-chip-grid>

    <mat-autocomplete #auto="matAutocomplete" (optionSelected)="addChip($event.option.value)">
      <mat-option *ngFor="let test of filteredLabTests | async" [value]="test">
        <div class="test-option">
          <span class="test-code">{{ test.orderCode }}</span>
          <span class="test-name">{{ test.name }}</span>
          <span class="test-mnemonic" *ngIf="test.mnemonic">({{ test.mnemonic }})</span>
          <span class="test-type" *ngIf="test.type">[{{ test.type }}]</span>
          <span class="micro-badge" *ngIf="test.isMicro" matTooltip="Microbiology test - requires specimen source">
            <mat-icon class="micro-icon">science</mat-icon>
            <span class="micro-text">Micro</span>
          </span>
        </div>
      </mat-option>
      <mat-option *ngIf="(filteredLabTests | async)?.length === 0 && testControl.value">
        Can't find the test you are looking for? <a (click)="openContactUsDialog($event)">Contact Us.</a>
      </mat-option>
    </mat-autocomplete>

    <mat-hint *ngIf="hintText && testChips.length">{{hintText}}</mat-hint>
    <mat-hint *ngIf="enableDragAndDrop">Drag and drop to reorder tests</mat-hint>
  </mat-form-field>
</div>

<!-- Contact Us Dialog -->
<ng-template #contactDialog>
  <h2 mat-dialog-title>Contact Us</h2>
  <div mat-dialog-content>
    <p>We'd love to hear from you! Please fill out the form below and we'll get back to you as soon as possible.</p>

    <mat-form-field class="full-width">
      <mat-label>Subject</mat-label>
      <input matInput [(ngModel)]="contactMessage.subject" placeholder="Enter subject">
    </mat-form-field>

    <mat-form-field class="full-width">
      <mat-label>Message</mat-label>
      <textarea matInput [(ngModel)]="contactMessage.message" placeholder="Enter your message" rows="4"></textarea>
    </mat-form-field>
  </div>

  <div mat-dialog-actions>
    <button mat-button (click)="closeDialog()">Cancel</button>
    <button mat-button color="primary" (click)="sendContactMessage()">Send</button>
  </div>
</ng-template>